import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Plus } from "lucide-react";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { getCategories } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

export default function Nav() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [cats, setCats] = useState([]);
  const location = useLocation();

  const NAV = useMemo(
    () => [
      { labelKey: "nav.products", to: "/products", mega: true, testId: "products" },
      {
        labelKey: "nav.coverageCalculators",
        to: "/calculators?tool=adhesive",
        testId: "coverage-calculators",
        submenu: [
          { labelKey: "nav.adhesiveCoverage", to: "/calculators?tool=adhesive", testId: "adhesive-coverage" },
          { labelKey: "nav.groutCoverage", to: "/calculators?tool=grout", testId: "grout-coverage" },
        ],
      },
      { labelKey: "nav.groutVisualizer", to: "/grout-visualizer", testId: "grout-visualizer" },
      { labelKey: "nav.about", to: "/about", testId: "about" },
      { labelKey: "nav.journal", to: "/blog", testId: "journal" },
      { labelKey: "nav.contact", to: "/contact", testId: "contact" },
    ],
    []
  );

  useEffect(() => {
    getCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setMega(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobile]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || mega ? "border-b border-black/10 bg-white/80 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-6 md:px-10">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-10 lg:flex" onMouseLeave={() => setMega(false)}>
          {NAV.map((item) => (
            <div
              key={item.to}
              className="group relative"
              onMouseEnter={() => setMega(item.mega ? true : false)}
            >
              <NavLink
                to={item.to}
                data-testid={`nav-${item.testId}`}
                className={({ isActive }) =>
                  `label relative py-2 transition-colors duration-200 hover:text-foreground ${
                    isActive ? "text-foreground" : "text-muted-foreground"
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
              {item.submenu ? (
                <div className="invisible absolute left-1/2 top-full z-50 mt-4 w-64 -translate-x-1/2 border border-black/10 bg-background opacity-0 shadow-sm transition-all duration-150 group-hover:visible group-hover:opacity-100">
                  {item.submenu.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to}
                      data-testid={`nav-submenu-${child.testId}`}
                      className="block border-b border-black/10 px-5 py-4 text-sm text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground last:border-b-0"
                    >
                      {t(child.labelKey)}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher className="hidden md:block" />
          <Link
            to="/contact"
            data-testid="nav-get-quote"
            className="group hidden items-center gap-2 bg-primary px-6 py-3 text-primary-foreground transition-colors duration-200 hover:bg-foreground hover:text-background md:inline-flex"
          >
            <span className="label text-[0.65rem]">{t("nav.getQuote")}</span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
          </Link>
          <button
            className="inline-flex h-11 w-11 items-center justify-center border border-black/15 text-foreground lg:hidden"
            onClick={() => setMobile(true)}
            data-testid="nav-mobile-open"
            aria-label={t("nav.openMenu")}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mega menu */}
      <AnimatePresence>
        {mega && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            onMouseEnter={() => setMega(true)}
            onMouseLeave={() => setMega(false)}
            className="absolute inset-x-0 top-20 hidden border-b border-black/10 bg-white/95 backdrop-blur-xl lg:block"
            data-testid="mega-menu"
          >
            <div className="mx-auto grid max-w-[1600px] grid-cols-4 gap-px bg-black/10 px-6 md:px-10">
              {cats.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/products?cat=${c.slug}`}
                  className="group relative bg-background p-8 transition-colors duration-200 hover:bg-secondary"
                  data-testid={`mega-cat-${c.slug}`}
                >
                  <span className="label text-primary">0{i + 1}</span>
                  <h4 className="mt-3 font-display text-xl font-semibold">{c.name}</h4>
                  <p className="mt-2 text-sm text-muted-foreground">{c.tagline}</p>
                  <ArrowUpRight className="absolute right-6 top-8 h-4 w-4 text-muted-foreground transition-all duration-200 group-hover:rotate-45 group-hover:text-primary" />
                </Link>
              ))}
              <Link
                to="/products"
                className="flex flex-col justify-between bg-primary p-8 text-primary-foreground"
                data-testid="mega-all-products"
              >
                <span className="label">{t("nav.catalogue")}</span>
                <span className="font-display text-2xl font-semibold leading-tight">
                  {t("nav.viewAllProducts")}
                </span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile drawer — portal to <body>, solid overlay (CSS entrance, no framer opacity) */}
      {mobile &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] bg-background lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo onClick={() => setMobile(false)} />
              <div className="flex items-center gap-3">
                <LanguageSwitcher />
                <button
                  className="inline-flex h-11 w-11 items-center justify-center border border-black/15"
                  onClick={() => setMobile(false)}
                  data-testid="nav-mobile-close"
                  aria-label={t("nav.closeMenu")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <nav className="flex flex-col px-6 pt-6">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobile(false)}
                  data-testid={`mobile-nav-${item.testId}`}
                  className="flex items-center justify-between border-b border-black/10 py-5 font-display text-4xl font-semibold duration-500 animate-in fade-in slide-in-from-bottom-4"
                >
                  {t(item.labelKey)}
                  <Plus className="h-6 w-6 text-primary" />
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobile(false)}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-primary px-6 py-4 text-primary-foreground"
                data-testid="mobile-get-quote"
              >
                <span className="label">{t("nav.getQuote")}</span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </nav>
          </div>,
          document.body
        )}
    </header>
  );
}
